import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useBlobUrl } from "../../../hooks/useBlobUrl";
import { DateiTypSymbol } from "../../documents/DateiTypSymbol";
import { isEditableImage } from "../../documents/documentKind";

/**
 * Größte Datei, für die die Verlaufskarte ein Vorschaubild lädt. Darüber
 * bleibt das Dateityp-Symbol — ein 30-MB-Foto für 48 Pixel zu holen lohnt nicht.
 */
export const VORSCHAUBILD_MAX_BYTES = 15 * 1024 * 1024;

/** Bekommt dieses Dokument im Verlauf ein Vorschaubild statt des Symbols? */
export function zeigtVorschaubild(d: {
    fileName: string;
    mimeType?: string | null;
    fileSize?: number | null;
}): boolean {
    if (!isEditableImage(d.fileName, d.mimeType)) return false;
    return d.fileSize == null || d.fileSize <= VORSCHAUBILD_MAX_BYTES;
}

/**
 * Das kleine Vorschaubild eines Bild-Dokuments in der Verlaufskarte
 * (User 22.10.2026: bisher stand dort nur das große PNG-Symbol). Geladen
 * wird erst, wenn die Karte in Sichtweite kommt — ein langer Verlauf voller
 * Fotos soll beim Öffnen nicht alle Dateien holen. Bis dahin, und wenn das
 * Laden scheitert, steht das Dateityp-Symbol wie bei jedem anderen Dokument.
 * Vergrößert wird über den Klick auf die Karte (Dokument-Viewer).
 */
export const DokumentVorschaubild: React.FC<{
    id: number;
    fileName: string;
    mimeType?: string | null;
}> = ({ id, fileName, mimeType }) => {
    const ref = useRef<HTMLSpanElement | null>(null);
    const [sichtbar, setSichtbar] = useState(() => typeof IntersectionObserver === "undefined");

    useEffect(() => {
        if (sichtbar) return;
        const el = ref.current;
        if (!el) return;
        const beobachter = new IntersectionObserver(
            (eintraege) => {
                if (eintraege.some((e) => e.isIntersecting)) {
                    setSichtbar(true);
                    beobachter.disconnect();
                }
            },
            { rootMargin: "200px" },
        );
        beobachter.observe(el);
        return () => beobachter.disconnect();
    }, [sichtbar]);

    const { url, error } = useBlobUrl(sichtbar ? `documents/${id}/download` : null);

    return (
        <span ref={ref} className="vg4-ek-vorschaubild-huelle">
            {url && !error ? (
                <img className="vg4-ek-vorschaubild" src={url} alt={fileName} title={fileName} draggable={false} />
            ) : (
                <DateiTypSymbol fileName={fileName} mimeType={mimeType} size={40} />
            )}
        </span>
    );
};
